import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import { useDispatch, useSelector } from "react-redux";
import { Dialog, Grid, IconButton, Radio } from "@mui/material";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { assignTraineeActions } from "../../Store/AssignTrainee";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import { useTranslation } from "react-i18next";
import {
  CustomButton,
  StyledDataGrid,
} from "components/RosterConfig/RosterConfigStyle";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import { red } from "@mui/material/colors";
import { Transition } from "components/Common/Header/Header";
import TextLabel from "components/Common/TextLabel/TextLabel";
import { ArrowBackOutlined, Close } from "@mui/icons-material";
import { CustomFooter } from "components/RosterConfig/RosterConfigInputs";
import { searchTrainee } from "services/userServices";
import RescheduleOrTransferDrawer from "components/RescheduleOrTransferDrawer/RescheduleOrTransferDrawer";
import BackdropComponent from "components/Spinner/Spinner";

const headCells = [
  {
    id: "oracleId",
    numeric: false,
    label: "Oracle ID",
  },
  {
    id: "name",
    numeric: false,
    label: "First Name",
  },
  {
    id: "lastName",
    numeric: false,
    label: "Last Name",
  },
  {
    id: "existsInMultipleRosters",
    numeric: false,
    disablePadding: true,
    label: "",
  },
  { id: "taleoStatus", numeric: false, label: "Taleo Status" },
  {
    id: "oracleStatus",
    numeric: false,
    label: "Oracle Status",
  },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// Since 2020 all major browsers ensure sort stability with Array.prototype.sort().
// stableSort() brings sort stability to non-modern browsers (notably IE11). If you
// only support modern browsers you can replace stableSort(exampleArray, exampleComparator)
// with exampleArray.slice().sort(exampleComparator)
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const multipleTraineeRedirection = (oracleId) => {
  window.open(`/searchResult/OracleId/${oracleId}`, "_self");
};

const TableSortLabelStyle = {
  "&.MuiTableSortLabel-root": {
    color: "black",
  },
  "&.Mui-active": {
    color: "black",
  },
  "& .MuiTableSortLabel-icon": {
    color: "#3299FF !important",
  },
};

const isExistsInMultipleRosters = (row) =>
  row?.existsInMultipleRosters !== null;

const isTerminatedAssignment = (row) =>
  row?.oracleStatus !== "Terminate Assignment";

const typographyStyle = {
  fontFamily: "Urbanist, Verdana, Arial, sans-serif",
  letterSpacing: "0.10rem",
  fontWeight: "bold",
  color: "#3F5765",
  paddingBottom: 1,
};

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="normal">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align="center"
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.id === "existsInMultipleRosters" ? (
              <></>
            ) : (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
                sx={TableSortLabelStyle}
              >
                <b>{headCell.label}</b>
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc"
                      ? "sorted descending"
                      : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

export default function TraineeSourceList(props) {
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("oracleId");
  const [selected, setSelected] = React.useState([]);
  const [oracleStatus, setOracleStatus] = React.useState("");
  const [rows, setRows] = React.useState([]);
  const [allRows, setAllRows] = React.useState([]);
  const [currentStatusFilter, setCurrentStatusFilter] = React.useState([]);
  const { t } = useTranslation(["AssignTrainees"]);
  const dispatch = useDispatch();
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [traineeRosterDetails, setTraineeRosterDetails] = React.useState([]);
  const [pageSize, setPageSize] = React.useState(10);
  const [selectedRosterId, setSelectedRosterID] = React.useState("");
  const [selectedTraineeDetails, setSelectedTraineeDetails] = React.useState({
    classTraineeId: "",
    oracleId: "",
    candidateId: "",
    preferredFirstName: "",
    preferredLastName: "",
    wave: "",
    parentClientName: "",
  });
  const [loading, setLoading] = React.useState(false);
  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const dense = false;
  const assignTraineeData = useSelector((state) => state.assignTrainees);
  const left = assignTraineeData?.sourceList;
  const unAssignedTraineesList = assignTraineeData.unAssignedTraineesList;

  const getItemOccurance = React.useCallback(
    (list) =>
      list.reduce((accumulator, value) => {
        accumulator[value] = ++accumulator[value] || 1;
        return accumulator;
      }, {}),
    [],
  );

  const getTraineeDetails = React.useCallback(
    (key, value) => {
      let details = "";
      const oracleId = value?.split("-").shift();
      const traineeData = unAssignedTraineesList.filter(
        (item) => item?.OracleId === oracleId,
      );
      const traineeObject = Object.assign({}, ...traineeData);
      switch (key) {
        case "name": {
          details = traineeObject?.FirstName;
          break;
        }
        case "lastName": {
          details = traineeObject?.LastName;
          break;
        }
        case "oracleStatus": {
          details = traineeObject?.EmployeeStatus;
          break;
        }
        case "currentStatus": {
          details = traineeObject?.TraineeStatus;
          break;
        }
        case "traineeStatus":
          details = traineeObject?.TraineeStatus;
          break;
        case "traineeStep":
          details = traineeObject?.TraineeStep;
          break;

        case "existsInMultipleRosters": {
          details = traineeObject?.Message_MultipleActiveRosters;
          break;
        }
        default:
          break;
      }
      return details;
    },
    [unAssignedTraineesList],
  );

  const createData = React.useCallback(
    (source) => {
      return source.map((item) => ({
        id: item,
        oracleId: item?.split("-")?.shift(),
        name: getTraineeDetails("name", item),
        lastName: getTraineeDetails("lastName", item),
        oracleStatus: getTraineeDetails("oracleStatus", item),
        currentStatus: getTraineeDetails("currentStatus", item),
        taleoStatus: `${getTraineeDetails("traineeStep", item) ?? ""} / ${getTraineeDetails("traineeStatus", item) ?? ""}`,
        traineeStatus: getTraineeDetails("traineeStatus", item),
        traineeStep: getTraineeDetails("traineeStep", item),
        existsInMultipleRosters: getTraineeDetails(
          "existsInMultipleRosters",
          item,
        ),
      }));
    },
    [getTraineeDetails],
  );

  const DEFAULT_TALEO_FILTER = "Hire/Hired";

  const defaultFilter = React.useCallback((source, allRows) => {
    if (source?.length !== 0) {
      const filteredData = allRows.filter(
        (item) =>
          `${item.traineeStep ?? ""}/${item.traineeStatus ?? ""}` ===
          DEFAULT_TALEO_FILTER,
      );
      setRows(filteredData.length > 0 ? filteredData : allRows);
      setOracleStatus(filteredData.length > 0 ? DEFAULT_TALEO_FILTER : "All");
    }
  }, []);

  const getCurrentStatusFilterSource = React.useCallback(
    (source) => {
      if (!source?.length) return [];

      const allCombined = source
        .filter((item) => item?.TraineeStep || item?.TraineeStatus)
        .map(
          (item) => `${item?.TraineeStep ?? ""}/${item?.TraineeStatus ?? ""}`,
        );

      const occurrenceMap = getItemOccurance(allCombined);

      const uniqueEntries = Object.entries(occurrenceMap);

      uniqueEntries.push(["All", source.length]);

      return uniqueEntries;
    },
    [getItemOccurance],
  );

  const resetDialogStates = () => {
    setSelectedRosterID("");
    setSelectedTraineeDetails({
      classTraineeId: "",
      oracleId: "",
      candidateId: "",
      preferredFirstName: "",
      preferredLastName: "",
      wave: "",
      parentClientName: "",
    });
    if (isDialogOpen === false) setTraineeRosterDetails([]);
    setPageSize(10);
  };

  React.useEffect(() => {
    if (isDialogOpen === false) {
      resetDialogStates();
    }
  }, [isDialogOpen]);

  React.useEffect(() => {
    const rowsData = createData(left);
    setCurrentStatusFilter(
      getCurrentStatusFilterSource(unAssignedTraineesList),
    );
    setRows(rowsData);
    setAllRows(rowsData);
    setSelected([]);
    dispatch(assignTraineeActions.setChecked([]));
    setOracleStatus("");
    defaultFilter(unAssignedTraineesList, rowsData);
  }, [
    unAssignedTraineesList,
    left,
    createData,
    dispatch,
    defaultFilter,
    getItemOccurance,
    getCurrentStatusFilterSource,
  ]);

  const visibleRows = React.useMemo(
    () => stableSort(rows, getComparator(order, orderBy)),
    [order, orderBy, rows],
  );

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const filterdRows = rows.filter(
        (row) => !isExistsInMultipleRosters(row) && isTerminatedAssignment(row),
      );
      const newSelected = filterdRows.map((n) => n.id);
      setSelected(newSelected);
      dispatch(assignTraineeActions.setChecked(newSelected));
      return;
    }
    setSelected([]);
    dispatch(assignTraineeActions.setChecked([]));
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
    dispatch(assignTraineeActions.setChecked(newSelected)); //Setting Checked globally
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const handleChange = (event) => {
    const val = event.target.value;
    let rowsData = [];

    if (!val || val === "All") {
      rowsData = allRows;
    } else {
      rowsData = allRows.filter(
        (item) =>
          `${item.traineeStep ?? ""}/${item.traineeStatus ?? ""}` === val,
      );
    }

    setRows(rowsData);
    setOracleStatus(val);
  };

  const getRowLength = () => {
    const filteredRows = rows.filter((row) => !isExistsInMultipleRosters(row));
    return filteredRows.length;
  };

  const handleRadioChange = (params) => {
    setSelectedTraineeDetails({
      ...selectedTraineeDetails,
      classTraineeId: params.row.classTraineeId,
      wave: params.row.wave,
      parentClientName: params.row.parentClientName,
    });
    setSelectedRosterID(params.row.classRosterId);
  };

  const renderSelectionCell = (params) => (
    <Radio onChange={() => handleRadioChange(params)}></Radio>
  );

  const columns = [
    {
      field: "id",
      headerName: "",
      flex: 0.2,
      sortable: false,
      renderCell: renderSelectionCell,
    },
    {
      field: "classRosterId",
      headerName: "Roster ID",
      flex: 0.3,
      sortable: false,
    },
    {
      field: "wave",
      headerName: "Wave #",
      flex: 0.3,
      sortable: false,
    },
    {
      field: "traineeStatus",
      headerName: "Trainee Status",
      flex: 0.4,
      sortable: false,
    },
    {
      field: "parentClientName",
      headerName: "Parent Client",
      flex: 1,
      sortable: false,
    },
  ];

  const prepareTraineeDetails = (data) => {
    return {
      oracleId: data?.oracleId,
      candidateId: data?.candidateId,
      preferredFirstName: data?.firstName,
      preferredLastName: data?.lastName,
    };
  };

  const fetchTraineeRosterDetails = async (row) => {
    setLoading(true);
    const userDetails = await searchTrainee("OracleId", row?.oracleId);
    if (userDetails.status === true) {
      setSelectedTraineeDetails(
        prepareTraineeDetails(userDetails.data[0].traineeDetail),
      );
      setTraineeRosterDetails(
        userDetails.data[0]?.rosterDetails?.map((item) => ({
          ...item,
          id: item.classRosterId,
        })),
      );
      setLoading(false);
    }
  };

  const handleRescheduleTransferPopup = (row) => {
    fetchTraineeRosterDetails(row);
    setIsDialogOpen(true);
  };

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Paper
        sx={{
          mb: 2,
          marginLeft: "10px",
          marginTop: "10px",
        }}
        elevation={2}
      >
        <Grid container>
          <Grid item xs={7}>
            {selected?.length > 0 ? (
              <Typography
                variant="subtitle1"
                sx={{ padding: 1, fontFamily: "Urbanist, sans-serif" }}
              >
                {" "}
                {`${selected?.length}/${allRows?.length} selected`}{" "}
              </Typography>
            ) : (
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: "bold",
                  fontFamily: "Urbanist, sans-serif",
                  color: "#3F5765",
                  marginTop: "10px",
                  marginLeft: "30px",
                }}
              >
                {" "}
                {t("Trainee details")}{" "}
              </Typography>
            )}
          </Grid>

          <Grid item xs={4.8} sx={{ marginTop: "15px" }}>
            <FormControl fullWidth size="small">
              <Typography
                size="small"
                sx={{
                  fontFamily: "Urbanist, sans-serif",
                  color: "#3F5765",
                  fontWeight: "bold",
                  fontSize: 14,
                }}
              >
                {t("Taleo status")}
              </Typography>
              <Select
                value={oracleStatus}
                onChange={handleChange}
                size="small"
                sx={{
                  fontFamily: "Urbanist, sans-serif",
                  color: "#3F5765",
                }}
              >
                {currentStatusFilter.map((item) => (
                  <MenuItem key={item[0]} value={item[0]}>
                    {item[0] === "All"
                      ? `All (${item[1]})`
                      : `${item[0].replace("/", " / ")} (${item[1]})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={0.2}></Grid>
        </Grid>

        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 280px)",
            marginLeft: "10px",
          }}
        >
          <Table
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
            stickyHeader
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              rowCount={getRowLength()}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = isSelected(row.id);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow
                    hover
                    {...(!isExistsInMultipleRosters(row) && {
                      onClick: (event) => handleClick(event, row.id),
                    })}
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <TableCell>
                      {isExistsInMultipleRosters(row) ? (
                        <IconButton
                          title="Reschedule / Transfer"
                          onClick={() => handleRescheduleTransferPopup(row)}
                        >
                          <PublishedWithChangesIcon
                            sx={{ color: "#3299FF", fontSize: 25 }}
                          />
                        </IconButton>
                      ) : (
                        <Checkbox
                          sx={{ marginLeft: 0.5 }}
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            "aria-labelledby": labelId,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: "#2B3A42",
                        fontFamily: "Urbanist, sans-serif",
                      }}
                      id={labelId}
                      scope="row"
                      padding="none"
                      align="center"
                    >
                      {row?.oracleId}
                    </TableCell>
                    <TableCell
                      id={labelId}
                      align="center"
                      sx={{
                        color: "#2B3A42",
                        fontFamily: "Urbanist, sans-serif",
                      }}
                    >
                      {row?.name}
                    </TableCell>
                    <TableCell
                      id={labelId}
                      align="center"
                      sx={{
                        color: "#2B3A42",
                        fontFamily: "Urbanist, sans-serif",
                      }}
                    >
                      {row?.lastName}
                    </TableCell>
                    <TableCell align="center">
                      {isExistsInMultipleRosters(row) && (
                        <IconButton title={row?.existsInMultipleRosters}>
                          <WarningAmberIcon
                            sx={{ color: red[700], fontSize: 25 }}
                            onClick={() =>
                              multipleTraineeRedirection(row.oracleId)
                            }
                          ></WarningAmberIcon>
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color: "#2B3A42",
                        fontFamily: "Urbanist, sans-serif",
                      }}
                    >
                      {row?.taleoStatus}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        color:
                          row?.oracleStatus == "Terminate Assignment"
                            ? "red"
                            : "#2B3A42",
                        fontFamily: "Urbanist, sans-serif",
                        fontWeight:
                          row?.oracleStatus == "Terminate Assignment"
                            ? "bold"
                            : "normal",
                      }}
                    >
                      {row?.oracleStatus}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Dialog
        open={isDialogOpen}
        fullWidth
        maxWidth={"lg"}
        TransitionComponent={Transition}
        onClose={() => setIsDialogOpen(false)}
      >
        <>
          {loading ? <BackdropComponent /> : null}
          <Grid container>
            <Grid item xs={11.5} sx={{ padding: 2 }}>
              <TextLabel
                text={"Reschedule / Transfer"}
                header
                fontSize={16}
              ></TextLabel>
            </Grid>
            <Grid item xs={0.5} sx={{ paddingTop: 2 }}>
              <IconButton onClick={() => setIsDialogOpen(false)}>
                <Close sx={{ color: "#3299ff" }} />
              </IconButton>
            </Grid>

            <Grid item xs={12} sx={{ padding: 2 }}>
              {selectedRosterId === "" ? (
                <>
                  <Typography sx={typographyStyle}>
                    {"Please Select a Roster to proceed further"}
                  </Typography>

                  <StyledDataGrid
                    sx={{
                      fontFamily: "Urbanist, Verdana, Arial, sans-serif",
                      letterSpacing: "0.10rem",
                    }}
                    autoHeight
                    rows={traineeRosterDetails}
                    columns={columns}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[5, 10, 25]}
                    disableSelectionOnClick
                    disableColumnMenu
                    components={{
                      Footer: () => (
                        <CustomFooter
                          pageSize={pageSize}
                          displayText={"Total records:"}
                          rowCount={traineeRosterDetails.length}
                          rowsPerPageLabel={"Rows per page"}
                        />
                      ),
                    }}
                  />
                </>
              ) : (
                <Grid container>
                  <Grid item xs={12}>
                    <Grid container>
                      <Grid item xs={1}>
                        <Grid item xs={1.5}>
                          <CustomButton
                            onClick={resetDialogStates}
                            sx={{ color: "#3299FF" }}
                            startIcon={<ArrowBackOutlined />}
                          >
                            Back
                          </CustomButton>
                        </Grid>
                      </Grid>
                      <Grid item xs={2.5} sx={{ paddingTop: 1 }}>
                        <Typography
                          sx={typographyStyle}
                        >{`Trainee : ${selectedTraineeDetails.preferredFirstName}  ${selectedTraineeDetails.preferredLastName}`}</Typography>
                      </Grid>
                      <Grid item xs={1.5} sx={{ paddingTop: 1 }}>
                        <Typography
                          sx={typographyStyle}
                        >{`RosterId : ${selectedRosterId}`}</Typography>
                      </Grid>
                      <Grid item xs={1.5} sx={{ paddingTop: 1 }}>
                        <Typography
                          sx={typographyStyle}
                        >{`Wave : ${selectedTraineeDetails.wave}`}</Typography>
                      </Grid>
                      <Grid item xs={5.5} sx={{ paddingTop: 1 }}>
                        <Typography
                          sx={typographyStyle}
                        >{`Parent Client : ${selectedTraineeDetails.parentClientName}`}</Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <RescheduleOrTransferDrawer
                      fromAssignTrainee={true}
                      classRosterId={selectedRosterId}
                      traineeDetails={[selectedTraineeDetails]} //Passing as array as Reschedule/Transfer component is expecting array of objects
                      onClose={() => setIsDialogOpen(false)}
                      backToRosterList={resetDialogStates}
                      refreshTraineeList={props.refreshTraineeList}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Grid>
        </>
      </Dialog>
    </>
  );
}
